import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Spinner } from "@/components/Common";
import { Settings as SettingsIcon, Save, Mail, Bell, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";

const BUNDESLAENDER = [
  "", "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg",
  "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen",
  "Rheinland-Pfalz", "Saarland", "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen",
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const loadStatus = () => api.get("/resend/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/settings").then((r) => { setS(r.data); setTestTo(r.data.reply_to || ""); });
    loadStatus();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        applicant_name: s.applicant_name, reply_to: s.reply_to,
        subject_template: s.subject_template, message_template: s.message_template,
        preferred_region: s.preferred_region, sender_email: s.sender_email,
        email_provider: s.email_provider,
      });
      toast.success("Impostazioni salvate");
      loadStatus();
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Inserisci un indirizzo email"); return; }
    setTesting(true);
    try {
      const r = await api.post("/resend/test", { to: testTo });
      if (r.data.status === "simulated") toast.warning("Modalità simulata (nessuna API key)");
      else toast.success("Email di test inviata!", { description: `Da ${r.data.sender}` });
    } catch (e) { toast.error("Test fallito", { description: e?.response?.data?.detail }); }
    finally { setTesting(false); }
  };

  if (!s) return <div className="p-10 flex justify-center"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-6 max-w-[820px]">
      <PageHeader title="Impostazioni" subtitle="Dati candidato, avvisi, mittente e template predefinito" icon={SettingsIcon} />

      {/* Preferred region / alerts */}
      <div className="card p-6 mb-4">
        <div className="flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-amber-400" /><h3 className="font-head font-semibold text-gray-200">Avvisi & Zona Preferita</h3></div>
        <label className="text-xs uppercase tracking-wider text-gray-500">Bundesland preferito (per gli avvisi di nuove offerte)</label>
        <select data-testid="settings-region" value={s.preferred_region || ""} onChange={(e) => setS({ ...s, preferred_region: e.target.value })}
          className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm">
          {BUNDESLAENDER.map((b) => <option key={b} value={b}>{b || "— Nessuna (nessun avviso) —"}</option>)}
        </select>
        <p className="text-xs text-gray-500 mt-1.5">Riceverai un avviso quando lo scraper trova nuove offerte di cucina in questa regione.</p>
      </div>

      {/* Applicant + template */}
      <div className="card p-6 space-y-5 mb-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500">Il tuo nome (candidato)</label>
          <input data-testid="settings-name" value={s.applicant_name || ""} onChange={(e) => setS({ ...s, applicant_name: e.target.value })}
            placeholder="Es. Mario Rossi" className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500">Email di risposta (Reply-To)</label>
          <input data-testid="settings-replyto" value={s.reply_to || ""} onChange={(e) => setS({ ...s, reply_to: e.target.value })}
            placeholder="la-tua-email@example.com" className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500">Oggetto predefinito</label>
          <input data-testid="settings-subject" value={s.subject_template} onChange={(e) => setS({ ...s, subject_template: e.target.value })}
            className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500">Messaggio predefinito</label>
          <textarea data-testid="settings-message" value={s.message_template} onChange={(e) => setS({ ...s, message_template: e.target.value })}
            rows={9} className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm font-mono leading-relaxed" />
          <p className="text-xs text-gray-500 mt-1.5">Variabili: <span className="text-amber-400 font-mono">{"{NOME_HOTEL}"}</span>, <span className="text-amber-400 font-mono">{"{CITTA}"}</span>, <span className="text-amber-400 font-mono">{"{RUOLO_CANDIDATURA}"}</span></p>
        </div>
      </div>

      {/* Email sending method */}
      <div className="card p-6 mb-4 border-amber-500/20">
        <div className="flex items-center gap-2 mb-3"><Send className="w-4 h-4 text-emerald-400" /><h3 className="font-head font-semibold text-gray-200">Metodo di Invio Email</h3></div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <button data-testid="provider-gmail" onClick={() => setS({ ...s, email_provider: "gmail" })}
            className={`text-left p-4 rounded-xl border transition-all ${s.email_provider === "gmail" ? "border-amber-500/50 bg-amber-500/10" : "border-white/10 bg-white/5 hover:border-white/20"}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-100">La mia Gmail</span>
              {status?.gmail_ready
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Pronta</span>
                : <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">Da configurare</span>}
            </div>
            <div className="text-xs text-gray-500 mt-1">Invia da {status?.gmail_address || "la tua Gmail"} verso qualsiasi hotel</div>
          </button>
          <button data-testid="provider-resend" onClick={() => setS({ ...s, email_provider: "resend" })}
            className={`text-left p-4 rounded-xl border transition-all ${s.email_provider !== "gmail" ? "border-amber-500/50 bg-amber-500/10" : "border-white/10 bg-white/5 hover:border-white/20"}`}>
            <div className="font-medium text-gray-100">Resend</div>
            <div className="text-xs text-gray-500 mt-1">Richiede un dominio verificato (no @gmail.com)</div>
          </button>
        </div>

        {s.email_provider === "gmail" ? (
          <div className="bg-[#0E1015] rounded-lg p-4 border border-white/5">
            <div className="text-sm text-gray-300 mb-2 font-medium">Invio dalla tua Gmail ({status?.gmail_address})</div>
            {status?.gmail_ready ? (
              <p className="text-sm text-emerald-400">Configurata e pronta: le candidature partiranno dalla tua Gmail e le risposte arriveranno nella tua casella.</p>
            ) : (
              <>
                <p className="text-sm text-gray-400 mb-2">Serve una <span className="text-gray-200">App Password</span> di Google (16 caratteri). Come ottenerla:</p>
                <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Attiva la <a href="https://myaccount.google.com/signinoptions/twosv" target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">Verifica in 2 passaggi</a> sul tuo account Google.</li>
                  <li>Vai su <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">App Passwords</a> e crea una password (nome: "KuechenChef").</li>
                  <li>Incollami qui in chat quella password di 16 caratteri: la configuro io in modo sicuro.</li>
                </ol>
              </>
            )}
          </div>
        ) : (
          <div className="bg-[#0E1015] rounded-lg p-4 border border-white/5">
            <p className="text-sm text-gray-400 leading-relaxed mb-3">
              Con Resend serve un <span className="text-gray-200">dominio verificato</span> (non è possibile usare @gmail.com). Verifica su
              <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-sky-400 hover:underline"> resend.com/domains</a>, poi imposta il mittente:
            </p>
            <label className="text-xs uppercase tracking-wider text-gray-500">Email mittente verificata</label>
            <input data-testid="settings-sender" value={s.sender_email || ""} onChange={(e) => setS({ ...s, sender_email: e.target.value })}
              placeholder="bewerbung@tuodominio.de" className="input-dark w-full px-3 py-2.5 mt-1.5 text-sm" />
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-white/10">
          <label className="text-xs uppercase tracking-wider text-gray-500">Invia email di test (metodo attivo: {status?.provider === "gmail" ? "Gmail" : "Resend"})</label>
          <div className="flex gap-2 mt-1.5">
            <input data-testid="settings-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="destinatario@example.com" className="input-dark flex-1 px-3 py-2.5 text-sm" />
            <button data-testid="settings-test-send" onClick={sendTest} disabled={testing}
              className="btn-copper px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 shrink-0">
              {testing ? <Spinner /> : <Send className="w-4 h-4" />} Invia test
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1.5">Salva le impostazioni prima di testare, così uso il metodo selezionato.</p>
        </div>
      </div>

      <button data-testid="settings-save" onClick={save} disabled={saving}
        className="btn-copper px-6 py-3 rounded-lg text-sm flex items-center gap-2">
        {saving ? <Spinner /> : <Save className="w-4 h-4" />} Salva Impostazioni
      </button>
    </div>
  );
}
